import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { Typography } from '@workspace/ui/components/Typography';
import { ChevronDown24, ChevronRight16 } from '@workspace/ui/icons';
import { cn } from '@workspace/ui/lib/utils';

import { company } from '~/company/Company';
import { useGlobal } from '~/hooks/useGlobal';
import { orderStore, userStore } from '~/stores';

interface HeaderDubaiExpressAddressProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children' | 'role'> {
  onClick: () => void;
}

/**
 * Delivery address button for the DubaiExpress header. Unlike the MCosmetic
 * variant, the mobile layout renders the address on a single line.
 */
export const HeaderDubaiExpressAddress =
  observer<HeaderDubaiExpressAddressProps>(({ className, ...props }) => {
    const { t } = useTranslation();
    const { isMobile } = useGlobal();

    const emptyAddressLabel =
      (company.addressSelectedAtCheckout && company.config.defaultCity) ||
      t('phrases:selectDeliveryAddress');

    const addressLabel =
      orderStore.etaCalculation && userStore.deliveryAddress
        ? userStore.deliveryAddress.address1
        : emptyAddressLabel;

    return (
      <div
        {...props}
        role="button"
        className={cn(
          'flex w-full cursor-pointer items-center truncate text-navigation-text-in-button transition-colors duration-300 group-data-[scrolled=true]:text-navigation-text-primary md:gap-1',
          className,
        )}
      >
        {isMobile ? (
          <>
            <Typography.Text100Semibold className="truncate">
              {addressLabel}
            </Typography.Text100Semibold>
            <ChevronRight16 className="min-w-4" />
          </>
        ) : (
          <>
            <Typography.Text200Medium className="line-clamp-2 truncate">
              {addressLabel}
            </Typography.Text200Medium>
            <ChevronDown24 className="min-w-6" />
          </>
        )}
      </div>
    );
  });
HeaderDubaiExpressAddress.displayName = 'HeaderDubaiExpressAddress';
